import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IosShareOutlined, MarkunreadMailboxOutlined } from '@mui/icons-material';
import { Box, Stack, Typography, Tooltip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { EditorRef } from 'react-email-editor';

import ContentTaggingButtonWithDialog from 'domains/content/components/ContentTaggingButtonWithDialog/ContentTaggingButtonWithDialog';
import { ApplicationAccessType, CommunicationType, ContentTypeForComparison, DirectMail } from 'domains/content/types';
import Button from 'domains/core/components/Button';
import ContentNameInput from 'domains/core/components/ContentNameInput';
import LanguageTabBar from 'domains/core/components/LanguageTabBar';
import PageHeader from 'domains/core/components/PageHeader';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import useDirectMail from 'hooks/queries/useDirectMail';
import useDirectMailPdfPreview from 'hooks/queries/useDirectMailPdfExport';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useQueryParams from 'hooks/useQueryParams';
import { DEFAULT_LANGUAGE_CODE } from 'models/languages';
import ContentStatusText, { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { resolveContentStatus } from 'pages/ContentPage/ContentTable/utils';

import DirectMailEditor from './DirectMailEditor';
import useDirectMailSubmission from './useDirectMailSubmission';

const ContentDirectMailBuilderPage = () => {
    // URL params
    const query = useQueryParams();
    const id = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    const isCopy = query.get('isCopy') === 'true';
    // Status from URL is prioritized over API response to handle race condition when converting
    // published content to draft - the API cache might return stale "published" status before refetch completes
    const statusFromUrl = query.get('status') as ContentStatus | null;

    const isUnsaved = id === 'default';

    // Determine component mode
    const isCreate = !isCopy && isUnsaved;
    const isUpdate = !isCopy && !isUnsaved;

    const { contentDraft: hasContentDraft, mlTemplateBuilder: isMlEnabled } = useFlags();
    const { canPublishContent, canManageContent } = useAccessCentrePermissions();
    const { data, isInitialLoading: isSavedDataLoading, error: savedDataError } = useDirectMail(Number(id), {
        enabled: !isUnsaved && id !== null,
    });
    const savedData = data as DirectMail;

    // Get initial values
    const initialName = useMemo(() => {
        if (isCopy) return `Copy of ${savedData?.name}`;
        if (isUnsaved) return 'Untitled';
        return savedData?.name;
    }, [isCopy, isUnsaved, savedData?.name]);

    const initialDesign = useMemo(() => {
        if (isUnsaved) return;
        return savedData?.design;
    }, [isUnsaved, savedData?.design]);

    const initialCommunicationType = useMemo(() => {
        if (isUnsaved) return CommunicationType.COMMERCIAL;
        return savedData?.communicationType;
    }, [isUnsaved, savedData?.communicationType]);

    const initialApplicationsAccess = useMemo(() => {
        if (isUnsaved) return ApplicationAccessType.OUTREACH;
        return savedData?.applicationsAccess;
    }, [isUnsaved, savedData?.applicationsAccess]);

    // Initialize state only after data is loaded for existing direct mails
    const shouldWaitForData = !isUnsaved && !isSavedDataLoading && !savedDataError;
    const isDataReady = isUnsaved || (shouldWaitForData && savedData);

    // Initial state values based on data readiness
    const initialNameState = !isDataReady ? 'Untitled' : initialName || 'Untitled';
    const initialDesignState = !isDataReady ? undefined : initialDesign;
    const initialCommunicationTypeState = !isDataReady
        ? CommunicationType.COMMERCIAL
        : initialCommunicationType || CommunicationType.COMMERCIAL;
    const initialApplicationsAccessState = !isDataReady
        ? [ApplicationAccessType.OUTREACH]
        : (initialApplicationsAccess?.split(',') as ApplicationAccessType[]) || [ApplicationAccessType.OUTREACH];

    const [name, setName] = useState(initialNameState);
    const [design, setDesign] = useState(initialDesignState);
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [communicationType, setCommunicationType] = useState(initialCommunicationTypeState);
    const [applicationsAccess, setApplicationsAccess] = useState(initialApplicationsAccessState);
    const [currentAction, setCurrentAction] = useState<'saving' | 'publishing' | null>(null);

    const editorRef = useRef<EditorRef>(null);
    const unlayer = editorRef.current?.editor;
    const [isEditorReady, setIsEditorReady] = useState(false);
    const [defaultHtml, setDefaultHtml] = useState('');
    const [defaultDesignData, setDefaultDesignData] = useState(null);

    // Multilingual state (only used when isMlEnabled)
    const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE_CODE);
    const [languages, setLanguages] = useState<string[]>([DEFAULT_LANGUAGE_CODE]);
    const [htmlByLanguage, setHtmlByLanguage] = useState<Record<string, string>>({});
    const [designByLanguage, setDesignByLanguage] = useState<Record<string, any>>({});

    // Update state when data loads or changes
    useEffect(() => {
        if (!isDataReady) return;

        setName(initialName || 'Untitled');
        setCommunicationType(initialCommunicationType || CommunicationType.COMMERCIAL);
        setApplicationsAccess(
            (initialApplicationsAccess?.split(',') as ApplicationAccessType[]) || [ApplicationAccessType.OUTREACH]
        );

        // Update design and reload editor if design has changed
        if (initialDesign !== design) {
            setDesign(initialDesign);
            // Reload editor with new design if it's ready
            if (unlayer && isEditorReady) {
                unlayer.loadDesign(initialDesign);
            }
        }

        // Initialize multilingual state from saved data
        if (isMlEnabled && savedData?.htmlJson) {
            const langs = Object.keys(savedData.htmlJson);
            setLanguages(langs.length > 0 ? langs : [DEFAULT_LANGUAGE_CODE]);
            setHtmlByLanguage(savedData.htmlJson);
            setDesignByLanguage(savedData.designJson || {});
            const initLang = langs.includes(DEFAULT_LANGUAGE_CODE) ? DEFAULT_LANGUAGE_CODE : langs[0];
            setSelectedLanguage(initLang);
        }
    }, [
        isDataReady,
        initialDesign,
        initialName,
        initialCommunicationType,
        initialApplicationsAccess,
        design,
        unlayer,
        isEditorReady,
        isCopy,
        savedData,
        isMlEnabled,
    ]);

    // Handle when editor is ready and capture default design/HTML
    const handleEditorReady = (data: any) => {
        setIsEditorReady(true);
        if (isUnsaved && data) {
            // For new direct mail, capture the default design and HTML
            setDefaultHtml(data.html || '');
            setDefaultDesignData(data.design || null);
        }
    };

    // API operations
    const { isCreateLoading, isUpdateLoading, createError, updateError, handleSave } = useDirectMailSubmission({
        applicationsAccess,
        communicationType,
        id,
        parentFolderId,
        isCreate,
        isCopy,
        setErrorMessage,
    });

    // Handle PDF Preview
    const { mutate: getPdfPreview, isLoading: isPdfPreviewLoading } = useDirectMailPdfPreview({
        onSuccess: (data: any) => {
            const pdfBlob = new Blob([Uint8Array.from(atob(data.pdf), (c) => c.charCodeAt(0))], {
                type: 'application/pdf',
            });
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank')?.focus();
        },
        onError: () => {
            setErrorMessage('An error occurred while generating the PDF preview');
        },
    });

    const exportPDF = () => {
        unlayer.exportHtml((data) => {
            if (data?.html) {
                getPdfPreview({ html: data.html });
            } else {
                setErrorMessage('Failed to export PDF');
            }
        });
    };

    // Handle language tab switch: flush current editor state into the map, load new language design
    const handleSelectLanguage = useCallback(
        (newLang: string) => {
            if (!unlayer || !isEditorReady) {
                setSelectedLanguage(newLang);
                return;
            }
            unlayer.exportHtml((data) => {
                setHtmlByLanguage((prev) => ({ ...prev, [selectedLanguage]: data?.html || '' }));
                setDesignByLanguage((prev) => ({ ...prev, [selectedLanguage]: data?.design || null }));
                setSelectedLanguage(newLang);
                const savedDesign = designByLanguage[newLang];
                if (savedDesign) {
                    unlayer.loadDesign(savedDesign);
                }
            });
        },
        [unlayer, isEditorReady, selectedLanguage, designByLanguage]
    );

    const handleExportHtml = (isExit?: boolean, status?: ContentStatus) => {
        const nameForAPI = name?.length ? name : 'Untitled';
        const isValidName = nameForAPI.length >= 3; // Simple validation: 3+ characters

        if (!unlayer || !isEditorReady) {
            // Editor not ready - use captured default data
            handleSave({
                name: nameForAPI,
                html: defaultHtml || '',
                design: defaultDesignData || design,
                isNameTooShort: !isValidName,
                isUpdate,
                saveMode: isExit ? 'exit' : 'save',
                status,
                ...(isMlEnabled && {
                    htmlMultilingual: { ...htmlByLanguage, [selectedLanguage]: defaultHtml || '' },
                    designMultilingual: { ...designByLanguage, [selectedLanguage]: defaultDesignData || design },
                }),
            });
        } else {
            unlayer.exportHtml((data) => {
                const currentHtml = data?.html || defaultHtml || '';
                const currentDesign = data?.design || defaultDesignData || design;
                handleSave({
                    name: nameForAPI,
                    html: currentHtml,
                    design: currentDesign,
                    isNameTooShort: !isValidName,
                    isUpdate,
                    saveMode: isExit ? 'exit' : 'save',
                    status,
                    ...(isMlEnabled && {
                        htmlMultilingual: { ...htmlByLanguage, [selectedLanguage]: currentHtml },
                        designMultilingual: { ...designByLanguage, [selectedLanguage]: currentDesign },
                    }),
                });
            });
        }
    };

    const handleSaveAndExitClick = () => {
        setCurrentAction('saving');
        handleExportHtml(true, resolveContentStatus(hasContentDraft, ContentStatus.DRAFT));
    };

    const handlePublishClick = () => {
        setCurrentAction('publishing');
        handleExportHtml(true, resolveContentStatus(hasContentDraft, ContentStatus.PUBLISHED));
    };

    const isNameUndefinedOnTouch = !name && isNameTouched && name !== 'Untitled';
    const isNameTooShort = isNameTouched && name !== 'Untitled' && name.length < 3;

    const getNameHelperText = () => {
        if (isNameUndefinedOnTouch && name !== 'Untitled') return 'Please enter a name';
        if (isNameTooShort) return 'Name must have at least 3 characters';
    };

    const getDisabledMessage = () => {
        if (!isLoading) return '';
        if (currentAction === 'publishing') return 'Content is publishing';
        if (currentAction === 'saving') return 'Content is saving';
        return 'Content is saving'; // Fallback
    };

    const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isNameTouched) {
            setIsNameTouched(true);
        }
        setName(e.target.value);
    };

    const handleCommunicationTypeChange = (e: any) => {
        setCommunicationType(e.target.value);
    };

    const handleApplicationsAccessChange = (e: any) => {
        setApplicationsAccess(e.target.value);
    };

    // Loading and error states
    const isLoading = isSavedDataLoading || isCreateLoading || isUpdateLoading;
    const isError = !!savedDataError || !!createError || !!updateError || !!errorMessage;
    const errorMessageText = savedDataError?.message || createError?.message || updateError?.message;

    // Reset currentAction when loading is complete
    useEffect(() => {
        if (!isLoading) {
            setCurrentAction(null);
        }
    }, [isLoading]);

    // Don't render editor until data is ready (prevents race conditions)
    const shouldRenderEditor = isDataReady;

    return (
        <Stack height="100%" mb={4} rowGap={4}>
            {isError && <ErrorSnackbar errorMessage={errorMessage ?? errorMessageText} />}
            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }}>
                <ContentNameInput
                    dataTestId="template-name-input"
                    error={name !== 'Untitled' && (isNameUndefinedOnTouch || isNameTooShort)}
                    helperText={getNameHelperText()}
                    name={name}
                    onChange={handleChangeName}
                    withClickAway
                />
                {hasContentDraft && (
                    <Box sx={{ pt: 3 }}>
                        <ContentStatusText
                            status={
                                id && id !== 'default' && !isCopy
                                    ? statusFromUrl || (savedData as any)?.status || ' '
                                    : ContentStatus.DRAFT
                            }
                        />
                    </Box>
                )}
            </Box>

            <Stack height="100%">
                <PageHeader
                    icon={<MarkunreadMailboxOutlined fontSize="inherit" />}
                    header={
                        <Stack
                            direction="row"
                            alignItems="center"
                            columnGap={1.5}
                            data-testid="directmail-content-header"
                        >
                            <Typography color="primary.main" variant="h4" whiteSpace="nowrap">
                                Direct Mail
                            </Typography>
                            <ContentTaggingButtonWithDialog
                                channel={ContentTypeForComparison.DIRECT_MAIL}
                                handleApplicationsAccess={handleApplicationsAccessChange}
                                handleCommunicationType={handleCommunicationTypeChange}
                                selectedApplicationsAccess={applicationsAccess}
                                selectedCommunicationType={communicationType}
                            />
                        </Stack>
                    }
                    headerOthers={
                        <Stack
                            direction="row"
                            alignItems="center"
                            columnGap={1}
                            ml={1}
                            justifyContent="flex-end"
                            width="100%"
                        >
                            <Button
                                disabled={!unlayer || isPdfPreviewLoading}
                                onClick={exportPDF}
                                variant="outlined"
                                startIcon={<IosShareOutlined />}
                            >
                                Export PDF
                            </Button>
                            {canManageContent && (
                                <Tooltip title={getDisabledMessage()}>
                                    <span>
                                        <Button
                                            disabled={isLoading}
                                            onClick={handleSaveAndExitClick}
                                            variant="outlined"
                                            data-testid="save-exit-button"
                                            role="button"
                                        >
                                            Save & Exit
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {hasContentDraft && canPublishContent && (
                                <Tooltip title={getDisabledMessage()}>
                                    <span>
                                        <Button
                                            disabled={isLoading}
                                            onClick={handlePublishClick}
                                            variant="contained"
                                            data-testid="publish-button"
                                            role="button"
                                        >
                                            Publish
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>
                    }
                />
                {isMlEnabled && (
                    <LanguageTabBar
                        languages={languages}
                        selectedLanguage={selectedLanguage}
                        onSelectLanguage={handleSelectLanguage}
                        onAddLanguage={(lang) => setLanguages((prev) => [...prev, lang])}
                        onDeleteLanguage={(lang) => {
                            setLanguages((prev) => prev.filter((l) => l !== lang));
                            setHtmlByLanguage(({ [lang]: _h, ...rest }) => rest);
                            setDesignByLanguage(({ [lang]: _d, ...rest }) => rest);
                            if (selectedLanguage === lang) {
                                setSelectedLanguage(DEFAULT_LANGUAGE_CODE);
                                const fallbackDesign = designByLanguage[DEFAULT_LANGUAGE_CODE];
                                if (unlayer && isEditorReady && fallbackDesign) {
                                    unlayer.loadDesign(fallbackDesign);
                                }
                            }
                        }}
                        disabled={isLoading}
                    />
                )}
                {shouldRenderEditor && (
                    <DirectMailEditor editorRef={editorRef} initialDesign={design} onEditorReady={handleEditorReady} />
                )}
            </Stack>
        </Stack>
    );
};

export default ContentDirectMailBuilderPage;
